import { useMemo } from 'react';
import { buildTerritorialOverview } from '../utils/buildTerritorialOverview';
import { buildNarrativeConvergence } from '../intelligence/buildNarrativeConvergence';

function TerritorialIntelligencePanel({
  projectActors = [],
  projectAlerts = [],
  projectSignals = [],
  projectTimeline = [],
  territorialFilters = {},
  activeProject,
  projectPrediction = null
}) {
  const overview = useMemo(() => buildTerritorialOverview({
    actors: projectActors,
    alerts: projectAlerts,
    signals: projectSignals,
    timeline: projectTimeline,
    filters: territorialFilters
  }), [projectActors, projectAlerts, projectSignals, projectTimeline, territorialFilters]);

  const convergence = useMemo(() => buildNarrativeConvergence({
    signals: projectSignals,
    alerts: projectAlerts,
    actors: projectActors,
    timeline: projectTimeline
  }), [projectActors, projectAlerts, projectSignals, projectTimeline]);

  const territorioActivo = [
    territorialFilters.selectedSector,
    territorialFilters.selectedComuna,
    territorialFilters.selectedRegion
  ].filter(Boolean).join(' · ');

  const estadoColor = {
    'Escalamiento Territorial': '#ef4444',
    'Convergencia Narrativa': '#f97316',
    'Activación Territorial': '#eab308',
    'Sensibilidad Creciente': '#22c55e',
    'Observación Activa': '#06b6d4',
    'Observación': '#06b6d4',
    'Estable': '#64748b'
  }[overview.estadoTerritorial] || '#64748b';

  const actoresCriticos = projectActors
    .filter((a) => {
      const estado = a._vita?.state?.state;
      return ['Escalamiento Territorial', 'Convergencia Narrativa',
        'Activación Territorial', 'Sensibilidad Creciente'].includes(estado);
    })
    .sort((a, b) => (b._vita?.scoring?.IVT || 0) - (a._vita?.scoring?.IVT || 0))
    .slice(0, 4);

  const narrativasDominantes = [
    overview.narrativaDominante,
    ...(overview.ecosistemaNarrativo?.secundarias || [])
  ].filter(Boolean).slice(0, 4);

  const relatoTerritorial = buildRelatoTerritorial({
    overview,
    convergence,
    actoresCriticos,
    territorioActivo
  });

  const tienePredicion = projectPrediction && !projectPrediction.insuficiente;

  return (
    <div style={styles.wrapper}>

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.contextBadge}>
            {territorioActivo
              ? `Territorio activo: ${territorioActivo}`
              : 'Visión territorial general'}
          </div>
          <div style={{
            ...styles.estadoBadge,
            background: `${estadoColor}22`,
            border: `1px solid ${estadoColor}55`,
            color: estadoColor
          }}>
            {overview.estadoTerritorial}
          </div>
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.lecturaBox}>
          <div style={styles.sectionLabel}>LECTURA TERRITORIAL EJECUTIVA</div>
          <p style={styles.lecturaText}>{overview.descripcionEstado}</p>
          <div style={styles.relatoBox}>
            <div style={styles.relatoLabel}>RELATO TERRITORIAL</div>
            <p style={styles.relatoText}>{relatoTerritorial}</p>
          </div>
        </div>

        <div style={styles.accionBox}>
          <div style={styles.sectionLabel}>ACCIÓN RECOMENDADA</div>
          <p style={styles.accionText}>{overview.accionSugerida}</p>
          <div style={styles.metricsRow}>
            <Metric label="Propagación" value={overview.propagacion} />
            <Metric label="Tendencia" value={overview.tendencia} />
            <Metric label="Riesgo" value={`${overview.riesgoGeneral}%`} />
          </div>
        </div>
      </div>

      <div style={styles.narrativasSection}>
        <div style={styles.sectionLabel}>NARRATIVAS TERRITORIALES ACTIVAS</div>
        <div style={styles.narrativasGrid}>
          {narrativasDominantes.length > 0
            ? narrativasDominantes.map((narrativa, i) => (
                <div key={i} style={{
                  ...styles.narrativaTag,
                  ...(i === 0 ? styles.narrativaDominante : {})
                }}>
                  {i === 0 && <span style={styles.dominanteDot}>●</span>}
                  {narrativa}
                </div>
              ))
            : <div style={styles.narrativaVacia}>Sin narrativas detectadas aún</div>
          }
        </div>
        {convergence.convergenceLevel !== 'Preliminar' && (
          <div style={styles.convergenciaLine}>
            Convergencia: <strong>{convergence.convergenceLevel}</strong>
            {' · '}{convergence.convergenceNarrative}
          </div>
        )}
      </div>

      {actoresCriticos.length > 0 && (
        <div style={styles.actoresSection}>
          <div style={styles.sectionLabel}>ACTORES TERRITORIALES CRÍTICOS</div>
          <div style={styles.actoresGrid}>
            {actoresCriticos.map((actor) => {
              const estado = actor._vita?.state?.state || actor.estadoTerritorial || '-';
              const ivt = actor._vita?.scoring?.IVT;
              const color = actor._vita?.state?.color || '#64748b';
              return (
                <div key={actor.id} style={styles.actorCard}>
                  <div style={{ ...styles.actorEstado, color, borderLeftColor: color }}>
                    {estado}
                  </div>
                  <div style={styles.actorNombre}>{actor.nombre}</div>
                  <div style={styles.actorMeta}>
                    {actor.sector || actor.comuna || '-'}
                    {ivt !== undefined && ` · IVT ${ivt}%`}
                  </div>
                  {actor._vita?.reasoning?.narrativaDominante && (
                    <div style={styles.actorNarrativa}>
                      {actor._vita.reasoning.narrativaDominante}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={styles.predictiveSection}>
        <div style={styles.sectionLabel}>RADAR PREDICTIVO TERRITORIAL</div>

        {!tienePredicion ? (
          <div style={styles.predictiveInsuficiente}>
            <span style={styles.predictiveInsuficienteIcon}>⟳</span>
            <div>
              <div style={styles.predictiveInsuficienteTitulo}>
                Acumulando datos históricos
              </div>
              <div style={styles.predictiveInsuficienteTexto}>
                {projectPrediction?.accionPrioritaria ||
                  'Edita y guarda stakeholders para activar el motor predictivo.'}
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.predictiveContent}>
            <div style={styles.predictiveHeader}>
              <div>
                <div style={styles.predictiveTendencia}>
                  Tendencia:{' '}
                  <strong style={{
                    color: projectPrediction.tendenciaGeneral === 'Escalando'
                      ? '#ef4444'
                      : projectPrediction.tendenciaGeneral === 'Estabilizando'
                        ? '#22c55e'
                        : '#eab308'
                  }}>
                    {projectPrediction.tendenciaGeneral}
                  </strong>
                </div>
                <div style={styles.predictiveActores}>
                  {projectPrediction.totalActoresConHistorial} actor(es) con historial ·
                  IVT proyectado 30d: <strong>{projectPrediction.IVTProyectadoPromedio}%</strong>
                </div>
              </div>
            </div>

            {projectPrediction.actoresEnEscalamiento.length > 0 && (
              <div style={styles.predictiveAlertBox}>
                <div style={styles.predictiveAlertTitle}>
                  ⚠ Actores proyectando escalamiento
                </div>
                {projectPrediction.actoresEnEscalamiento.map((actor, i) => (
                  <div key={i} style={styles.predictiveActorRow}>
                    <strong style={{ color: 'white' }}>{actor.nombre}</strong>
                    <span style={styles.predictiveActorMeta}>
                      IVT actual {actor.IVTActual}% →
                      proyectado {actor.IVTProyectado30}% en 30d ·
                      Estado: {actor.estadoProyectado}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div style={styles.predictiveAccion}>
              {projectPrediction.accionPrioritaria}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

function buildRelatoTerritorial({ overview, convergence, actoresCriticos, territorioActivo }) {
  const territorio = territorioActivo || 'el territorio';
  const estado = overview.estadoTerritorial;
  const narrativa = overview.narrativaDominante;
  const actores = actoresCriticos.slice(0, 2).map((a) => a.nombre).join(' y ');

  if (estado === 'Escalamiento Territorial') {
    return `${territorio} enfrenta una situación de escalamiento territorial activo. La narrativa dominante "${narrativa}" ha convergido entre actores clave${actores ? ` — especialmente ${actores}` : ''} — generando presión territorial que requiere atención inmediata.`;
  }
  if (estado === 'Convergencia Narrativa') {
    return `En ${territorio} se observa convergencia narrativa en torno a "${narrativa}". ${actores ? `Actores como ${actores} están alineando discursos` : 'Los actores territoriales están alineando discursos'}, lo que puede acelerar la movilización territorial si no se gestiona preventivamente.`;
  }
  if (estado === 'Activación Territorial') {
    return `${territorio} muestra señales de activación territorial. La narrativa "${narrativa}" está ganando tracción${actores ? ` entre actores como ${actores}` : ''}. Se recomienda intervención preventiva antes de que escale.`;
  }
  if (estado === 'Sensibilidad Creciente') {
    return `${territorio} presenta sensibilidad territorial creciente. La narrativa "${narrativa}" circula entre los actores del territorio. El momento es oportuno para gestión preventiva y diálogo temprano.`;
  }
  if (narrativa && narrativa !== 'Sin narrativa dominante detectada') {
    return `${territorio} se encuentra en observación. Se detecta la narrativa "${narrativa}" como señal inicial. No hay convergencia relevante aún, pero es recomendable mantener monitoreo activo.`;
  }
  return `${territorio} presenta condiciones territoriales estables. No se detectan narrativas dominantes ni señales de activación. Mantener monitoreo regular.`;
}

function Metric({ label, value }) {
  return (
    <div style={styles.metric}>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricLabel}>{label}</div>
    </div>
  );
}

const styles = {
  wrapper: {
    background: 'linear-gradient(135deg,#071a2f 0%,#0b2442 50%,#04101d 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '24px',
    padding: '28px',
    marginBottom: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '22px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  contextBadge: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: 'rgba(255,255,255,0.72)',
    padding: '6px 14px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '700'
  },
  estadoBadge: {
    padding: '6px 14px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '900'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '22px'
  },
  lecturaBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '18px',
    padding: '20px'
  },
  accionBox: {
    background: 'rgba(6,182,212,0.07)',
    border: '1px solid rgba(103,232,249,0.15)',
    borderRadius: '18px',
    padding: '20px'
  },
  sectionLabel: {
    color: '#67e8f9',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1.2px',
    marginBottom: '10px'
  },
  lecturaText: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '15px',
    lineHeight: '1.7',
    margin: '0 0 16px'
  },
  relatoBox: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '12px',
    padding: '14px',
    borderLeft: '3px solid rgba(103,232,249,0.4)'
  },
  relatoLabel: {
    color: '#94a3b8',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1px',
    marginBottom: '8px'
  },
  relatoText: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: 0,
    fontStyle: 'italic'
  },
  accionText: {
    color: 'white',
    fontSize: '15px',
    lineHeight: '1.6',
    margin: '0 0 18px',
    fontWeight: '500'
  },
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: '10px'
  },
  metric: {
    background: 'rgba(255,255,255,0.06)',
    borderRadius: '12px',
    padding: '12px',
    textAlign: 'center'
  },
  metricValue: {
    color: 'white',
    fontSize: '18px',
    fontWeight: '900',
    marginBottom: '4px'
  },
  metricLabel: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '11px',
    fontWeight: '700'
  },
  narrativasSection: {
    marginBottom: '22px'
  },
  narrativasGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '12px'
  },
  narrativaTag: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: 'rgba(255,255,255,0.82)',
    padding: '8px 14px',
    borderRadius: '999px',
    fontSize: '13px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  narrativaDominante: {
    background: 'rgba(6,182,212,0.12)',
    border: '1px solid rgba(103,232,249,0.28)',
    color: '#dffafe',
    fontWeight: '800'
  },
  dominanteDot: {
    color: '#67e8f9',
    fontSize: '10px'
  },
  narrativaVacia: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: '13px',
    fontStyle: 'italic'
  },
  convergenciaLine: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  actoresSection: {
    marginTop: '4px'
  },
  actoresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
    gap: '12px'
  },
  actorCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '16px'
  },
  actorEstado: {
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    borderLeft: '3px solid',
    paddingLeft: '8px'
  },
  actorNombre: {
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '4px'
  },
  actorMeta: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '12px',
    marginBottom: '8px'
  },
  actorNarrativa: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '8px',
    padding: '6px 10px',
    color: 'rgba(255,255,255,0.68)',
    fontSize: '12px',
    fontStyle: 'italic'
  },
  predictiveSection: {
    marginTop: '22px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    paddingTop: '20px'
  },
  predictiveInsuficiente: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px dashed rgba(255,255,255,0.10)',
    borderRadius: '16px',
    padding: '18px'
  },
  predictiveInsuficienteIcon: {
    fontSize: '24px',
    color: 'rgba(255,255,255,0.3)',
    flexShrink: 0
  },
  predictiveInsuficienteTitulo: {
    color: 'rgba(255,255,255,0.72)',
    fontWeight: '700',
    fontSize: '14px',
    marginBottom: '4px'
  },
  predictiveInsuficienteTexto: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  predictiveContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  predictiveHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  predictiveTendencia: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '16px',
    marginBottom: '4px'
  },
  predictiveActores: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px'
  },
  predictiveAlertBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.20)',
    borderRadius: '14px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  predictiveAlertTitle: {
    color: '#fca5a5',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  predictiveActorRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  predictiveActorMeta: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '12px'
  },
  predictiveAccion: {
    background: 'rgba(6,182,212,0.08)',
    border: '1px solid rgba(103,232,249,0.15)',
    borderRadius: '14px',
    padding: '14px 16px',
    color: 'rgba(255,255,255,0.82)',
    fontSize: '14px',
    lineHeight: '1.6'
  }
};

export default TerritorialIntelligencePanel;